import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { API_URLS, END_POINTS } from '../globalConfig/api.config';
import type { FeatureFlags } from '../models/dto';

const CACHE_DURATION_MS = 10 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  /**
   * Last-loaded flag state, kept in sync with the instance signal below.
   * Plain (non-DI) config objects like sidebar-menu.config.ts can't `inject()` this
   * service, so they read this static snapshot instead — same pattern as
   * PermissionService.isOperationsAdmin.
   */
  private static latestFlags: FeatureFlags = {};

  static isFlagEnabled(flagName: string): boolean {
    return !!FeatureFlagService.latestFlags[flagName];
  }

  private readonly http = inject(HttpClient);
  private readonly flags = signal<FeatureFlags>({});
  private cache$: Observable<FeatureFlags> | null = null;
  private cacheExpiresAt = 0;

  /** Fetches feature flag state from the backend, serving a cached response for up to 10 minutes. */
  getFlags(): Observable<FeatureFlags> {
    if (this.cache$ && Date.now() < this.cacheExpiresAt) {
      return this.cache$;
    }

    this.cache$ = this.http
      .get<FeatureFlags>(`${API_URLS.API_URL}${END_POINTS.FEATURE_FLAGS}`)
      .pipe(
        tap((flags) => {
          this.flags.set(flags ?? {});
          FeatureFlagService.latestFlags = flags ?? {};
          this.cacheExpiresAt = Date.now() + CACHE_DURATION_MS;
        }),
        catchError(() => {
          // Don't poison the cache with a failed response — let the next caller retry immediately.
          this.cache$ = null;
          return of(this.flags());
        }),
        shareReplay(1)
      );

    return this.cache$;
  }

  /** Reactively checks a single flag, triggering a fetch if the cache is cold or expired. */
  isEnabled$(flagName: string): Observable<boolean> {
    return this.getFlags().pipe(map((flags) => !!flags[flagName]));
  }

  /** Synchronous read of the last-loaded flag state; false until getFlags()/isEnabled$() has resolved at least once. */
  isEnabled(flagName: string): boolean {
    return !!this.flags()[flagName];
  }
}
